import {NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Routes, RouterModule} from '@angular/router';
import { ClickOutsideModule } from 'ng-click-outside';
import { ClipboardModule } from 'ngx-clipboard';

import {DonationsPage} from './donations/donations.page';
import {MailPage} from './mail/mail.page';
import {UiModule} from '../ui/ui.module';
import {SelectModule} from 'ng-select';
import {FormsModule} from '@angular/forms';
import {HomePage} from './home/home.page';
import {ProfilePage} from './profile/profile.page';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {AuthGuard} from '../core/guards/auth.guard';
import {CoreModule} from '../core/core.module';
import {ProfileMainTab} from './profile/main.tab';
import {ProfileSettingsTab} from './profile/settings.tab';
import {EditProfilePage} from './home/edit.page';
import {TeamsPageComponent} from './teams/teams.component';
import {ConstituentDataResolver} from '../core/resolver/constituent-data-resolver.service';
import {ProfileEmergencyTab} from './profile/emergency.contact.tab';
import {InsuranceTab} from './profile/insurance.tab';
import { HelpFrameComponent } from './help/helpframe.component';
import { EditPageComponent } from './edit-page/edit-page.component';
import { PostsComponent } from './edit-page/posts/posts.component';
import { CommentsComponent } from './edit-page/comments/comments.component';
import { PageOptionsComponent } from './edit-page/page-options/page-options.component';
import { EmailTemplatesComponent } from './email-templates/email-templates.component';
import { TemplateComponent } from './email-templates/template.component';
import {NgxMaskModule} from 'ngx-mask';
import {NewlinePipe} from './email-templates/newline.pipe';
import { ParticipantResourcesComponent } from './fundraising-toolbox/participants-resources/participants-resources.component';
import { FirstHoursComponent } from './fundraising-toolbox/participants-resources/first-hours/first-hours.component';
import { FiveStepsComponent } from './fundraising-toolbox/participants-resources/five-steps/five-steps.component';
import { DownloadsComponent } from './fundraising-toolbox/participants-resources/downloads/downloads.component';
import { TeamCaptainsGuideComponent } from './fundraising-toolbox/team-captains-guide/team-captains-guide.component';
import { FiveStepsTeamComponent } from './fundraising-toolbox/team-captains-guide/five-steps-team/five-steps-team.component';
import { DownloadsTeamComponent } from './fundraising-toolbox/team-captains-guide/downloads-team/downloads-team.component';
import { PreviewComponent } from './edit-page/preview/preview.component';
import {CommentsResolver} from '../core/resolver/comments.resolver';
import {CommonDataResolver} from '../core/resolver/common-data.resolver';
import {ToastrModule} from 'ngx-toastr';
import {SafePipe} from './edit-page/preview/safe.pipe';
import { CKEditorModule } from 'ng2-ckeditor';
import {EditPostComponent} from './edit-page/edit-post/edit-post';
import {ImageUploadModule} from 'angular2-image-upload';
import { EditNewsFeedComponent } from './edit-page/edit-news-feed/edit-news-feed.component';
import { UnsavedChangesGuard } from '../core/guards/unsaved-changes.guard';
import { DonationAmountsResolver } from '../core/resolver/donation-amounts.resolver';
import { EmailTemplatesResolver } from '../core/resolver/email-templates.resolver';
import { ConstituentAddressBooksResolver } from '../core/resolver/constituent-address-books.resolver';
import { ToolboxContentResolver } from '../core/resolver/toolbox-content.resolver';
import { DatagridComponent } from '../ui/datagrid/datagrid.component';
import { PersonalComponent } from './profile/main-tab/personal.component';
import { PhoneComponent } from './profile/main-tab/phone.component';
import { EmailComponent } from './profile/main-tab/email.component';
import { AddressComponent } from './profile/main-tab/address.component';
import { FacebookFundraiserComponent } from './facebook-fundraiser/facebook-fundraiser.component';
import { FacebookFundraiserPageComponent } from './facebook-fundraiser-page/facebook-fundraiser-page.component';
import { FacebookFundraiserLinkComponent } from './facebook-fundraiser-link/facebook-fundraiser-link.component';
import {AdminPasswordComponent} from './admin-password/admin-password.component';
import {MainComponent} from './main/main.component';
import {NationalTeamComponent} from './national-team/national-team.component';
import {FacebookDialogComponent} from './facebook-dialog/facebook-dialog.component';
import {CountdownTimerComponent} from './countdown-timer/countdown-timer.component';
import {IeModalComponent} from './facebook-fundraiser-link/ie-modal.component';

export const routes: Routes = [
    {
        path: '',
        canActivate: [AuthGuard],
        resolve: {
            common: CommonDataResolver
        },
        children: [
            {
                path: '',
                resolve: {
                    constituentData: ConstituentDataResolver
                },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'main'
                    },
                    {
                        path: 'donations',
                        component: DonationsPage,
                        data: {
                            isDonationPage: true
                        }
                    },
                    {
                        path: 'matchingGifts',
                        component: DonationsPage,
                        data: {
                            isDonationPage: false
                        }
                    },
                    {
                        path: 'home',
                        component: HomePage
                    },
                    {
                        path: 'main',
                        component: MainComponent
                    },
                    {
                        path: 'profile',
                        component: ProfilePage,
                        canDeactivate: [ UnsavedChangesGuard ]
                    },
                    {
                        path: 'mail',
                        component: MailPage,
                        resolve: {
                            constituentAddressBooks: ConstituentAddressBooksResolver
                        }
                    },
                    {
                        path: 'emailTemplates/:eventId',
                        component: EmailTemplatesComponent,
                        resolve: {
                            emailTemplates: EmailTemplatesResolver
                        },
                        data: {
                            canReuse: false
                        }
                    },
                    {
                        path: 'teams',
                        component: TeamsPageComponent
                    },
                    {
                        path: 'help',
                        component: HelpFrameComponent
                    },
                    {
                        path: 'edit/:pageID',
                        component: EditPageComponent,
                        canDeactivate: [ UnsavedChangesGuard ],
                        resolve: {
                            donationAmounts: DonationAmountsResolver,
                            comments: CommentsResolver
                        },
                        data: {
                            canReuse: false
                        }
                    },
                    {
                        path: 'facebook/fundraiser/:facebookFundraiserID',
                        component: FacebookFundraiserPageComponent
                    },
                    {
                        path: 'facebook/fundraiser',
                        component: FacebookFundraiserPageComponent
                    },
                    {
                        path: 'fundraisingToolbox/:eventId/:isTeam',
                        component: ParticipantResourcesComponent,
                        resolve: {
                            fundraisingToolboxContent: ToolboxContentResolver
                        },
                        data: {
                            canReuse: false
                        }
                    },
                    {
                        path: 'national/teams/:teamId',
                        component: NationalTeamComponent
                    }
                ]
            }
        ]
    }
];


@NgModule({
    imports: [
        CommonModule,
        UiModule,
        FormsModule,
        SelectModule,
        NgbModule,
        NgxMaskModule.forRoot(),
        RouterModule.forChild(routes),
        CoreModule,
        ClipboardModule,
        ToastrModule,
        CKEditorModule,
        ImageUploadModule,
        ClickOutsideModule
    ],
    declarations: [
        DonationsPage,
        EditProfilePage,
        HomePage,
        PersonalComponent,
        PhoneComponent,
        EmailComponent,
        AddressComponent,
        ProfileMainTab,
        ProfileEmergencyTab,
        ProfileSettingsTab,
        InsuranceTab,
        ProfilePage,
        MailPage,
        TeamsPageComponent,
        HelpFrameComponent,
        NewlinePipe,
        SafePipe,
        EditPageComponent,
        PostsComponent,
        CommentsComponent,
        PageOptionsComponent,
        EmailTemplatesComponent,
        TemplateComponent,
        ParticipantResourcesComponent,
        FirstHoursComponent,
        FiveStepsComponent,
        DownloadsComponent,
        TeamCaptainsGuideComponent,
        FiveStepsTeamComponent,
        DownloadsTeamComponent,
        PreviewComponent,
        EditPostComponent,
        EditNewsFeedComponent,
        FacebookFundraiserComponent,
        FacebookFundraiserPageComponent,
        FacebookFundraiserLinkComponent,
        FacebookDialogComponent,
        AdminPasswordComponent,
        MainComponent,
        NationalTeamComponent,
        CountdownTimerComponent,
        IeModalComponent
    ],
    exports: [
        TemplateComponent
    ]
})
export class FundraisingModule { }
